import { ComponentType, Component } from 'react';
import { action, observable, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { Popover, PopoverProps } from '@servicetitan/design-system';
//

export interface PopoverTriggerProps {
    /* Trigger component should call onClick when clicked for Popover to toggle */
    onClick(): void;
    /* isOpen can be used to customize Trigger look when Popover is open */
    isOpen: boolean;
}

export interface PopoverSelfManagedProps extends Omit<PopoverProps, 'trigger'> {
    onOpen?(): void;
    onClose?(): void;
    trigger: ComponentType<PopoverTriggerProps>;
    wrapperClassName?: string;
}

/*
 * This component is intended to auto-manage Popover state in most usual scenario:
 * * Toggle on trigger click
 * * Close on click outside
 */

@observer
export class PopoverSelfManaged extends Component<PopoverSelfManagedProps> {
    @observable isOpen = false;
    shouldSkipClick = false;

    constructor(props: PopoverSelfManagedProps) {
        super(props);
        makeObservable(this);
    }

    @action toggleOpen = () => {
        this.isOpen = !this.isOpen;

        if (this.isOpen) {
            this.props.onOpen?.();
        } else {
            this.props.onClose?.();
        }
    };

    // click skipping works perfect with both portal and non-portal Popovers
    skipTheClick = () => (this.shouldSkipClick = true);

    @action handleClick = () => {
        if (this.shouldSkipClick) {
            this.shouldSkipClick = false;
        } else {
            this.isOpen = false;
            this.props.onClose?.();
        }
    };

    componentDidMount() {
        window.addEventListener('click', this.handleClick);
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.handleClick);
    }

    render() {
        const { wrapperClassName, trigger: Trigger, ...popoverProps } = this.props;
        return (
            <div onClick={this.skipTheClick} className={wrapperClassName}>
                <Popover
                    trigger={<Trigger onClick={this.toggleOpen} isOpen={this.isOpen} />}
                    open={this.isOpen}
                    {...popoverProps}
                />
            </div>
        );
    }
}
